// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useSchedulerExecutionDailyReports from '../../hooks/useSchedulerExecutionDailyReports';
import { formatOutcomeExecutionTooltipLabel } from '../../utils/schedulerExecutionDailyChart';

const CHART_SECTION_HEIGHT_PX = 240;
const BAR_CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 4 };
const LEGEND_WRAPPER_STYLE = { fontSize: '0.85rem' };
const AXIS_TICK = { fontSize: 11 };
const Y_AXIS_TICK = { fontSize: 12 };
const FAILED_BAR_RADIUS = [4, 4, 0, 0];

function formatOutcomeTooltipFormatter(value, name) {
  return [Number(value).toLocaleString(), name];
}

function OutcomeFacetChart({
  title,
  rows,
  outcomeLabels,
  tooltipContentStyle,
  matchFill,
  noMatchFill,
  failedFill,
}) {
  return (
    <Box>
      <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ width: '100%', height: CHART_SECTION_HEIGHT_PX }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={BAR_CHART_MARGIN}>
            <XAxis dataKey="dateLabelShort" tick={AXIS_TICK} />
            <YAxis tick={Y_AXIS_TICK} allowDecimals={false} width={40} />
            <Tooltip
              contentStyle={tooltipContentStyle}
              formatter={formatOutcomeTooltipFormatter}
              labelFormatter={formatOutcomeExecutionTooltipLabel}
            />
            <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} />
            <Bar
              stackId="outcome"
              name={outcomeLabels.match}
              dataKey="match"
              fill={matchFill}
            />
            <Bar
              stackId="outcome"
              name={outcomeLabels.noMatch}
              dataKey="noMatch"
              fill={noMatchFill}
            />
            <Bar
              stackId="outcome"
              name={outcomeLabels.failed}
              dataKey="failed"
              fill={failedFill}
              radius={FAILED_BAR_RADIUS}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

OutcomeFacetChart.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      dateKey: PropTypes.string,
      dateLabelShort: PropTypes.string,
      match: PropTypes.number,
      noMatch: PropTypes.number,
      failed: PropTypes.number,
    }),
  ).isRequired,
  outcomeLabels: PropTypes.shape({
    match: PropTypes.string.isRequired,
    noMatch: PropTypes.string.isRequired,
    failed: PropTypes.string.isRequired,
  }).isRequired,
  tooltipContentStyle: PropTypes.shape({
    backgroundColor: PropTypes.string,
    border: PropTypes.string,
    borderRadius: PropTypes.number,
    color: PropTypes.string,
  }).isRequired,
  matchFill: PropTypes.string.isRequired,
  noMatchFill: PropTypes.string.isRequired,
  failedFill: PropTypes.string.isRequired,
};

function SchedulerExecutionOutcomeChart() {
  const theme = useTheme();
  const { facets, loading, error } = useSchedulerExecutionDailyReports();
  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" gutterBottom component="div">
        Daily crawl outcomes (UTC)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        From Firestore{' '}
        <code>{'schedulerExecutionReports'}</code>
        {' '}
        · daily rollup faceted by crawl type (match / no-match / failures)
      </Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={32} />
        </Box>
      ) : null}
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unable to query scheduler execution reports. Confirm deployed scheduled reporter (<code>{'reportSchedulerExecutions'}</code>) and Firestore rules.
        </Alert>
      ) : null}
      {!loading && !error && facets.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No daily reports yet — the scheduled reporter writes one document per UTC day after the first run.
        </Typography>
      ) : null}
      {!loading && !error && facets.length > 0 ? (
        <Grid container spacing={2}>
          {facets.map(function renderOutcomeFacet(facet) {
            return (
              <Grid key={facet.crawlType} item xs={12} md={6}>
                <OutcomeFacetChart
                  title={facet.title}
                  rows={facet.rows}
                  outcomeLabels={facet.outcomeLabels}
                  tooltipContentStyle={tooltipContentStyle}
                  matchFill={theme.palette.success.main}
                  noMatchFill={theme.palette.warning.main}
                  failedFill={theme.palette.error.main}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : null}
    </Box>
  );
}

export default SchedulerExecutionOutcomeChart;
